// Document Management — the drive itself: a rail holding the tree, a list of what is in the open folder,
// and a details pane over the top.
//
// Every row the server sends already carries the caller's own level on it, so the controls a person
// cannot use are not rendered rather than rendered and refused. The server checks again regardless.

import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { strings } from '/imports/strings';
import { formatBytes, formatDateTime } from '/imports/utils';
import { FileMeta } from '/imports/types';
import { css } from '/imports/ui/css';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Input } from '/imports/ui/Input';
import { Modal } from '/imports/ui/Modal';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { Popover, useAnchorRect } from '/imports/ui/Popover';
import { FilesInput } from '/imports/ui/FilesInput';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { toast } from 'react-toastify';
import { toastOnError } from '/imports/ui/utils';
import { useMethod } from '/imports/hooks';
import {
  DriveFileRow, DriveFolderRow, DriveSearchHit, driveCreateFile, driveCreateFolder, driveDeleteFile,
  driveDeleteFolder, driveFolderPermissions, driveListFolder, driveMoveFile, driveMoveFolder, driveRenameFile,
  driveRenameFolder, driveSearch, driveStarFile, driveTrashFile, driveTrashFolder, driveZipFolder,
} from '/imports/api/drive';
import { DriveMenuStyled, DriveName, DrivePageStyled, DriveTarget, useDriveAccess } from '/imports/ui/DriveShared';
import { DriveGrid } from '/imports/ui/DriveGrid';
import { DriveFolderTree } from '/imports/ui/DriveFolderTree';
import { DriveDetailsBlade } from '/imports/ui/DriveDetailsBlade';
import { DrivePermissions } from '/imports/ui/DrivePermissions';
import { DriveShareModal } from '/imports/ui/DriveShareModal';

type View = 'folders' | 'starred' | 'trash';
type Target = DriveTarget;

const VIEW_MODE_KEY = 'drive.viewMode';
/** How many rows a listing asks for, and how much more "load more" asks for. */
const PAGE = 200;

/** The last view mode this browser chose. A folder of photographs and a folder of contracts want
 *  different answers, and the person switching between them should only have to say so once. */
const readViewMode = (): 'list' | 'grid' => {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === 'grid' ? 'grid' : 'list';
  }
  catch {
    return 'list';
  }
};

export const DocumentsPage = () => {
  const { canAccess, canManageOrg } = useDriveAccess();
  const navigate = useNavigate();
  const { folderId } = useParams();
  const [view, setView] = useState<View>('folders');
  const [search, setSearch] = useState<string>();
  const [reload, setReload] = useState(0);
  const [detailFileId, setDetailFileId] = useState<string>();
  const [menu, setMenu] = useState<{ target: Target, el: HTMLElement }>();
  const [renaming, setRenaming] = useState<Target>();
  const [renameTo, setRenameTo] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [moving, setMoving] = useState<Target>();
  const [permissions, setPermissions] = useState<DriveFolderRow>();
  const [sharing, setSharing] = useState<Target>();
  const [confirming, setConfirming] = useState<Target>();
  const [uploading, setUploading] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(readViewMode);
  const [limit, setLimit] = useState(PAGE);
  const menuAnchor = useAnchorRect(menu?.el);

  const query = search?.trim() || '';
  // Two characters, the same floor the server holds: one would match most of a corpus.
  const searching = query.length > 1;
  const [listing, loading] = useMethod(driveListFolder, {
    folderId: view === 'folders' ? folderId : undefined,
    trashed: view === 'trash',
    starred: view === 'starred',
    limit,
  }, [folderId, view, reload, limit], () => !searching);
  const [results, searchLoading] = useMethod(driveSearch, {
    query, folderId: view === 'folders' ? folderId : undefined,
  }, [query, folderId, view, reload], () => searching);
  const [folderAcl] = useMethod(driveFolderPermissions, { _id: permissions?._id || '' }, [permissions?._id], () => !!permissions);

  const changed = () => setReload(reload + 1);
  const openFolder = (id?: string) => {
    setView('folders');
    setSearch(undefined);
    setLimit(PAGE);
    thumbsRefetched.current = false;
    navigate(id ? `/documents/${id}` : '/documents');
  };

  // A thumbnail url expires like everything else the module hands out, so a page left open long enough
  // shows broken images. The first one to fail asks for a fresh listing; only the first, because a blob
  // that is genuinely missing would otherwise put this in a loop.
  const chooseView = (mode: 'list' | 'grid') => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, mode);
    }
    catch {
      /* storage off, or a private window: the choice just does not outlive the tab */
    }
  };

  const thumbsRefetched = useRef(false);
  const thumbExpired = () => {
    if (thumbsRefetched.current) return;
    thumbsRefetched.current = true;
    changed();
  };

  const act = (run: () => Promise<unknown>) => toastOnError(async () => {
    await run();
    setMenu(undefined);
    changed();
  })();

  // FilesInput reports its finished uploads CUMULATIVELY — after the second file it hands back both, and
  // so on — so a handler that files the whole list each time would file the first one again. The second
  // attempt then fails on bytes the first already moved out of /nfs/uploads, and the thrown error
  // abandons the rest of the batch. Hence: file each upload once, keyed by the url its slug makes unique,
  // and let one bad file fail on its own.
  const filed = useRef(new Set<string>());
  const upload = toastOnError(async (files: FileMeta[]) => {
    const fresh = files.filter((file) => file.url && !filed.current.has(file.url));
    if (!folderId || !fresh.length) return;
    for (const file of fresh) filed.current.add(file.url);
    setUploading(true);
    try {
      for (const file of fresh) {
        try {
          await driveCreateFile.call({ folderId, file });
        }
        catch (error: any) {
          toast.error(`${file.name}: ${error?.reason || error?.message || error}`);
        }
      }
      changed();
    }
    finally {
      setUploading(false);
    }
  });

  const newFolder = toastOnError(async (name: string) => {
    if (!name.trim()) return;
    const id = await driveCreateFolder.call({ parentId: folderId, name });
    setCreating(false);
    setRenameTo(undefined);
    changed();
    // A new root has nothing open beneath it, so land in it rather than leaving the person at the top.
    if (!folderId) openFolder(id);
  });

  const rename = toastOnError(async () => {
    const name = (renameTo || '').trim();
    if (!renaming || !name) return;
    if (renaming.kind === 'folder') await driveRenameFolder.call({ _id: renaming.row._id, name });
    else await driveRenameFile.call({ _id: renaming.row._id, name });
    setRenaming(undefined);
    changed();
  });

  const zip = toastOnError(async (folder: DriveFolderRow) => {
    setZipping(true);
    try {
      const made = await driveZipFolder.call({ folderId: folder._id });
      toast(strings.DriveZipReady(made.files, made.skipped));
      // The zip lives for ten minutes, so it is fetched now rather than handed over as a link to keep.
      window.open(made.url, '_blank', 'noreferrer');
    }
    finally {
      setZipping(false);
    }
  });

  const star = toastOnError(async (row: DriveFileRow) => {
    await driveStarFile.call({ _id: row._id, starred: !row.starred });
    changed();
  });

  if (!canAccess) return <NotPermittedPage />;

  const inTrash = view === 'trash';
  const canWriteHere = !!listing?.canCreateFolder;
  const target = menu?.target;
  const folderRows: DriveFolderRow[] = (searching ? results?.folders : listing?.folders) || [];
  const fileRows: (DriveFileRow & Partial<DriveSearchHit>)[] = (searching ? results?.files : listing?.files) || [];
  const empty = !loading && !searchLoading && !folderRows.length && !fileRows.length;

  return (
    <DrivePageStyled breadcrumbs={[{ path: '/documents', label: strings.Documents() }]}>
      <PageHeading left={<h1>{strings.DocumentManagement()}</h1>} />
      <PageBody>
        <div className="drive">
          <div className="drive-rail">
            <button type="button" className={`rail-link ${view === 'folders' && !folderId ? 'active' : ''}`} onClick={() => openFolder(undefined)}>
              <Icon icon="folder" size={16} fill={css.grey3} />{strings.AllDocuments()}
            </button>
            <button type="button" className={`rail-link ${view === 'starred' ? 'active' : ''}`} onClick={() => { setView('starred'); setSearch(undefined); }}>
              <Icon icon="star" size={16} fill={css.grey3} />{strings.Starred()}
            </button>
            <button type="button" className={`rail-link ${view === 'trash' ? 'active' : ''}`} onClick={() => { setView('trash'); setSearch(undefined); }}>
              <Icon icon="cancel" size={16} fill={css.grey3} />{strings.Trash()}
            </button>
            <h3>{strings.Folders()}</h3>
            <DriveFolderTree selectedId={view === 'folders' ? folderId : undefined} reload={reload} onSelect={openFolder} />
          </div>

          <div className="drive-main">
            <div className="drive-bar">
              <Input
                className="search"
                icon="search"
                placeholder={view === 'folders' && folderId ? strings.DriveSearchHint() : strings.DriveSearchAll()}
                value={search || ''}
                onChange={(value) => setSearch(value)}
                onClear={() => setSearch(undefined)}
              />
              <span className="spacer" />
              <span className="view-toggle">
                <button
                  type="button"
                  className={`icon-btn ${viewMode === 'list' ? 'on' : ''}`}
                  aria-label={strings.ViewAsList()}
                  onClick={() => chooseView('list')}
                >
                  <Icon icon="menu" size={16} fill={css.grey3} />
                </button>
                <button
                  type="button"
                  className={`icon-btn ${viewMode === 'grid' ? 'on' : ''}`}
                  aria-label={strings.ViewAsGrid()}
                  onClick={() => chooseView('grid')}
                >
                  <Icon icon="category" size={16} fill={css.grey3} />
                </button>
              </span>
              {!inTrash && view === 'folders' && folderId && !searching && (
                <Button
                  className="secondary"
                  disabled={zipping}
                  onClick={() => zip({ _id: folderId, name: listing?.crumbs.at(-1)?.name || '' } as DriveFolderRow)}
                >
                  {strings.DownloadFolder()}
                </Button>
              )}
              {!inTrash && view === 'folders' && (canWriteHere || canManageOrg) && (
                <Button className="secondary" onClick={() => { setCreating(true); setRenameTo(undefined); }}>{strings.NewFolder()}</Button>
              )}
            </div>

            {view === 'folders' && !!listing?.crumbs.length && (
              <div className="crumbs">
                <button type="button" className="crumb" onClick={() => openFolder(undefined)}>{strings.AllDocuments()}</button>
                {listing.crumbs.map((crumb, index) => (
                  <React.Fragment key={crumb._id}>
                    <span className="sep">/</span>
                    <button
                      type="button"
                      className="crumb"
                      disabled={index === listing.crumbs.length - 1}
                      onClick={() => openFolder(crumb._id)}
                    >
                      {crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {viewMode === 'grid' ? (
              <DriveGrid
                folders={folderRows}
                files={fileRows}
                inTrash={inTrash}
                onOpenFolder={openFolder}
                onOpenFile={setDetailFileId}
                onMenu={(target, el) => setMenu({ target, el })}
                onStar={star}
                onThumbExpired={thumbExpired}
              />
            ) : (
              <table className="drive-list">
                <thead>
                  <tr>
                    <th>{strings.Name()}</th>
                    <th className="num">{strings.Size()}</th>
                    <th>{strings.Updated()}</th>
                    <th className="row-tools" />
                  </tr>
                </thead>
                <tbody>
                  {folderRows.map((row) => (
                    <tr key={row._id} onClick={() => (inTrash ? undefined : openFolder(row._id))}>
                      <td className="name">
                        <DriveName name={row.name} folder />
                        {!row.inherited && !!row.parentId && <Icon icon="lock" size={12} fill={css.grey4} />}
                      </td>
                      <td className="num muted">{row.files || ''}</td>
                      <td className="muted">{row.path}</td>
                      <td className="row-tools">
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={strings.Options()}
                          onClick={(e) => { e.stopPropagation(); setMenu({ target: { kind: 'folder', row }, el: e.currentTarget }); }}
                        >
                          <Icon icon="tune" size={16} fill={css.grey3} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {fileRows.map((row) => (
                    <tr key={row._id} onClick={() => setDetailFileId(row._id)}>
                      <td className="name">
                        <DriveName name={row.name} ext={row.ext} thumbUrl={row.thumbUrl} />
                        {!!row.snippet && <div className="snippet">{row.snippet}</div>}
                      </td>
                      <td className="num muted">{formatBytes(row.size || 0)}</td>
                      <td className="muted">{searching ? row.path : formatDateTime(row.updatedAt)}</td>
                      <td className="row-tools">
                        <button
                          type="button"
                          className={`icon-btn star ${row.starred ? 'on' : ''}`}
                          aria-label={strings.Starred()}
                          onClick={(e) => { e.stopPropagation(); star(row); }}
                        >
                          <Icon icon={row.starred ? 'starFilled' : 'star'} size={16} fill={row.starred ? '#e3a008' : css.grey5} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label={strings.Options()}
                          onClick={(e) => { e.stopPropagation(); setMenu({ target: { kind: 'file', row }, el: e.currentTarget }); }}
                        >
                          <Icon icon="tune" size={16} fill={css.grey3} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {empty && (
              <div className="empty">
                {searching ? strings.NoSearchResults()
                  : inTrash ? strings.TrashIsEmpty()
                    : view === 'starred' ? strings.DriveNothingStarred()
                      : folderId ? strings.EmptyFolder() : strings.DriveNoFolderSelected()}
              </div>
            )}
            {searching && results?.truncated && <div className="empty">{strings.SearchTruncated()}</div>}

            {!searching && !!listing && listing.total > fileRows.length && (
              <div className="listing-foot">
                <span>{strings.ShowingOf(fileRows.length, listing.total)}</span>
                <Button className="secondary" disabled={loading} onClick={() => setLimit(limit + PAGE)}>
                  {strings.LoadMore()}
                </Button>
              </div>
            )}

            {view === 'folders' && folderId && canWriteHere && !searching && (
              <>
                <div className="upload-caption muted">{strings.DriveUploadHere()}</div>
                <FilesInput disabled={uploading} onChange={(files) => upload(files)} />
              </>
            )}
          </div>
        </div>

        {menu && target && menuAnchor && (
          <Popover anchor={menuAnchor} trigger={menu.el} width={220} onClose={() => setMenu(undefined)}>
            <DriveMenuStyled>
              {target.kind === 'folder' && !inTrash && (
                <>
                  <button type="button" className="menu-item" onClick={() => { openFolder(target.row._id); setMenu(undefined); }}>
                    <Icon icon="folder" size={16} fill={css.grey3} />{strings.OpenFolder()}
                  </button>
                  <button
                    type="button"
                    className="menu-item"
                    disabled={zipping}
                    onClick={() => { zip(target.row as DriveFolderRow); setMenu(undefined); }}
                  >
                    <Icon icon="download" size={16} fill={css.grey3} />{strings.DownloadFolder()}
                  </button>
                </>
              )}
              {target.kind === 'file' && !inTrash && (
                <button type="button" className="menu-item" onClick={() => { setDetailFileId(target.row._id); setMenu(undefined); }}>
                  <Icon icon="visibility" size={16} fill={css.grey3} />{strings.Details()}
                </button>
              )}
              {!inTrash && target.row.access !== 'viewer' && (
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => { setRenaming(target); setRenameTo(target.row.name); setMenu(undefined); }}
                >
                  <Icon icon="edit" size={16} fill={css.grey3} />{strings.Rename()}
                </button>
              )}
              {!inTrash && target.row.access === 'manager' && (
                <button type="button" className="menu-item" onClick={() => { setMoving(target); setMenu(undefined); }}>
                  <Icon icon="send" size={16} fill={css.grey3} />{strings.MoveTo()}
                </button>
              )}
              {!inTrash && target.row.access === 'manager' && (
                <button type="button" className="menu-item" onClick={() => { setSharing(target); setMenu(undefined); }}>
                  <Icon icon="link2" size={16} fill={css.grey3} />{strings.ShareLink()}
                </button>
              )}
              {!inTrash && target.kind === 'folder' && target.row.access === 'manager' && (
                <button type="button" className="menu-item" onClick={() => { setPermissions(target.row); setMenu(undefined); }}>
                  <Icon icon="lock" size={16} fill={css.grey3} />{strings.Permissions()}
                </button>
              )}
              {target.row.access === 'manager' && <div className="menu-sep" />}
              {!inTrash && target.row.access === 'manager' && (
                <button
                  type="button"
                  className="menu-item danger"
                  onClick={() => act(() => (target.kind === 'folder'
                    ? driveTrashFolder.call({ _id: target.row._id, trashed: true })
                    : driveTrashFile.call({ _id: target.row._id, trashed: true })))}
                >
                  <Icon icon="cancel" size={16} fill={css.red4} />{strings.MoveToTrash()}
                </button>
              )}
              {inTrash && target.row.access === 'manager' && (
                <>
                  <button
                    type="button"
                    className="menu-item"
                    onClick={() => act(() => (target.kind === 'folder'
                      ? driveTrashFolder.call({ _id: target.row._id, trashed: false })
                      : driveTrashFile.call({ _id: target.row._id, trashed: false })))}
                  >
                    <Icon icon="switchAccess" size={16} fill={css.grey3} />{strings.RestoreItem()}
                  </button>
                  <button type="button" className="menu-item danger" onClick={() => { setConfirming(target); setMenu(undefined); }}>
                    <Icon icon="cancel" size={16} fill={css.red4} />{strings.DeleteForever()}
                  </button>
                </>
              )}
            </DriveMenuStyled>
          </Popover>
        )}

        {renaming && (
          <Modal
            title={strings.Rename()}
            onClose={() => setRenaming(undefined)}
            buttons={(
              <>
                <Button className="secondary" onClick={() => setRenaming(undefined)}>{strings.Cancel()}</Button>
                <Button onClick={() => rename()}>{strings.Save()}</Button>
              </>
            )}
          >
            <Input autoFocus value={renameTo || ''} onEnter={() => rename()} onChange={(value) => setRenameTo(value)} />
          </Modal>
        )}

        {creating && (
          <Modal
            title={strings.NewFolder()}
            onClose={() => setCreating(false)}
            buttons={(
              <>
                <Button className="secondary" onClick={() => setCreating(false)}>{strings.Cancel()}</Button>
                <Button onClick={() => newFolder(renameTo || '')}>{strings.Create()}</Button>
              </>
            )}
          >
            <Input autoFocus value={renameTo || ''} onEnter={(value) => newFolder(value || '')} onChange={(value) => setRenameTo(value)} />
          </Modal>
        )}

        {moving && (
          <Modal title={strings.MoveToFolder()} onClose={() => setMoving(undefined)}>
            {moving.kind === 'folder' && canManageOrg && (
              <DriveMenuStyled>
                <button
                  type="button"
                  className="menu-item"
                  onClick={() => act(async () => {
                    await driveMoveFolder.call({ _id: moving.row._id, parentId: undefined });
                    setMoving(undefined);
                  })}
                >
                  <Icon icon="folder" size={16} fill={css.grey3} />{strings.MoveToRoot()}
                </button>
              </DriveMenuStyled>
            )}
            <DriveFolderTree
              reload={reload}
              onSelect={(parentId) => act(async () => {
                if (moving.kind === 'folder') await driveMoveFolder.call({ _id: moving.row._id, parentId });
                else await driveMoveFile.call({ _id: moving.row._id, folderId: parentId });
                setMoving(undefined);
              })}
            />
          </Modal>
        )}

        {permissions && (
          <Modal title={`${strings.Permissions()} — ${permissions.name}`} onClose={() => setPermissions(undefined)}>
            <DrivePermissions
              kind="folder"
              _id={permissions._id}
              acl={folderAcl?.acl || []}
              ownAcl={folderAcl?.ownAcl}
              canManage={permissions.access === 'manager'}
              onChanged={() => { setPermissions(undefined); changed(); }}
            />
          </Modal>
        )}

        {sharing && (
          <DriveShareModal
            target={{ kind: sharing.kind, id: sharing.row._id }}
            name={sharing.row.name}
            onClose={() => { setSharing(undefined); changed(); }}
          />
        )}

        {confirming && (
          <ConfirmModal
            title={strings.DeleteForever()}
            body={confirming.kind === 'folder'
              ? strings.DriveDeleteFolderConfirm(confirming.row.name)
              : strings.DriveDeleteFileConfirm(confirming.row.name)}
            confirmText={strings.DeleteForever()}
            onClose={() => setConfirming(undefined)}
            onConfirm={() => act(async () => {
              if (confirming.kind === 'folder') await driveDeleteFolder.call({ _id: confirming.row._id });
              else await driveDeleteFile.call({ _id: confirming.row._id });
              setConfirming(undefined);
            })}
          />
        )}

        {detailFileId && (
          <DriveDetailsBlade
            fileId={detailFileId}
            reload={reload}
            onClose={() => setDetailFileId(undefined)}
            onChanged={changed}
          />
        )}
      </PageBody>
    </DrivePageStyled>
  );
};
